import { useCallback } from 'react';
import { useAppDownloadModal } from '../component';
import { bridge, isWeb } from '../utils';
import { convertDeeplinkToWebLink } from '../utils';
import { useRootStackNavigation } from './useRootNavigation';
import { resolveScreenFromPath } from '../webRoutePaths';
import { resolveHomeTabFromDeeplink } from '../utils/deeplinkToHomeTab';
import { isNotNil } from '../utils';
import { useExperimentGroup } from '../context/UserContext';

export const useOpenDeeplink = () => {
  const { show: showAppDownloadModal } = useAppDownloadModal();
  const navigation = useRootStackNavigation();

  const isMainRN = useExperimentGroup({
    sno: 'EXP1795',
    targetGroups: ['B'],
  });
  const moveWithReactNavigation = useCallback(
    async (link: string) => {
      // Home 탭에 대응되는 딥링크는 bridge 우회하고 RN navigation으로 직접 처리
      const homeTab = resolveHomeTabFromDeeplink(link);
      if (isNotNil(homeTab)) {
        navigation.navigate('Home', {
          selected: homeTab.screenName,
          ...homeTab.params,
        });
        return;
      }
      if (!isWeb) {
        if (link.startsWith('ably://webview')) {
          // ably webview 인 경우
          navigation.push('WebViewScreen', {
            url: link.replace('ably://webview?url=', ''),
          });
          return;
        }
      }
      const webLink = await convertDeeplinkToWebLink(link);
      if (webLink == null) {
        showAppDownloadModal();
        return;
      }

      // 외부/절대 URL은 location.href로 이동
      if (webLink.startsWith('http')) {
        if (link.startsWith('ably://webview')) {
          // ably webview 인 경우
          navigation.push('WebViewScreen', {
            url: webLink,
          });
          return;
        }
        location.href = webLink;
        return;
      }

      // TODO 나중엔 요걸 webCallback내부에서 하지 않고, bridge.openDeeplink 대신 호출하도록 수정함
      // 내부 경로: webRoutePaths에서 screen name 매칭 → RN 네비게이션
      const resolved = resolveScreenFromPath(webLink);
      if (resolved != null) {
        // navigation.push는 screen별로 discriminated union의 params 타입을 받지만,
        // resolved.screen은 union이라 TS가 narrow하지 못함. webRoutePaths와 각
        // ScreenItem의 contract가 런타임에 매칭되는 게 보장되므로 `as never` 캐스트.
        navigation.push(resolved.screen, {
          instanceId: `${resolved.screen}-${Date.now()}`,
          ...resolved.pathParams,
          ...resolved.queryParams,
        });
        return;
      }

      // 매칭 안 되면 fallback
      location.href = webLink;
    },
    [navigation, showAppDownloadModal],
  );

  const openDeeplink = useCallback(
    (link: string) => {
      if (isMainRN) {
        moveWithReactNavigation(link);
        return;
      }
      bridge.openDeeplink(link, {
        webCallback: () => moveWithReactNavigation(link),
      });
    },
    [isMainRN, moveWithReactNavigation],
  );

  return { openDeeplink };
};
